import { ConfigHelper } from '@devxdiscover/devhub-common';
import { extractGitHubSlug } from './utils/resourceFormatting';

/**
 * Represents an Active Directory group resource
 */
export type ADGroupResource = {
  name: string;
  location: string;
  status: 'provisioning' | 'active' | 'failed';
  members?: number;
};

/**
 * Represents a GitHub repository resource
 */
export type GitHubResource = {
  name: string;
  slug?: string; // GitHub org/repo slug (e.g., "comcast-enterprise/cool-app")
  location: string;
  status: 'provisioning' | 'active' | 'failed';
  visibility?: 'private' | 'public' | 'internal';
};

/**
 * Represents a CNAP tenancy resource
 */
export type CNAPResource = {
  name: string;
  location: string;
  status: 'provisioning' | 'active' | 'failed';
  region?: string;
};

/**
 * Container for all provisioned resources for an experimental application
 */
export type ExperimentalResources = {
  adGroups?: ADGroupResource[];
  github?: GitHubResource;
  githubRepos?: GitHubResource[]; // Array of GitHub repositories from backend
  cnap?: CNAPResource;
};

/**
 * New type matching the getIdeas() API response structure
 */
export type IdeaFromAPI = {
  userLogin: string;
  applicationId: number;
  applicationName: string;
  description: string;
  createdAt: string;
  owner: string; // Owner login
  updatedAt: string;
  resources: {
    adGroups: Array<{
      groupId: string;
      groupName: string;
    }>;
    tenantName?: string;
    githubRepo?: string;
    githubRepoUrl?: string;
  };
};

export type IdeaFromAPIResponse = {
  applications: IdeaFromAPI[];
};

/**
 * Represents an experimental application created through the onboarding flow
 */
export type ExperimentalApplication = {
  applicationId: number;
  name: string;
  description: string;
  status: 'Experimental';
  owner: {
    login: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  resources: ExperimentalResources;
};

/**
 * Mock data for experimental applications
 */
export const mockExperimentalApplications: ExperimentalApplication[] = [
  {
    applicationId: 67001,
    name: 'AI Customer Insights',
    description:
      'Exploring machine learning models to analyze customer behavior patterns',
    status: 'Experimental',
    owner: {
      login: 'john_doe',
      firstName: 'John',
      lastName: 'Doe',
      email: 'john_doe@comcast.com',
    },
    createdAt: '2025-10-15T10:30:00Z',
    updatedAt: '2025-10-15T10:30:00Z',
    resources: {
      adGroups: [
        {
          name: 'APP_67001_ADMIN',
          location: 'https://ad.comcast.com/group/APP_67001_ADMIN',
          status: 'active',
          members: 3,
        },
        {
          name: 'APP_67001_USERS',
          location: 'https://ad.comcast.com/group/APP_67001_USERS',
          status: 'active',
          members: 5,
        },
        {
          name: 'APP_67001_COLLABS',
          location: 'https://ad.comcast.com/group/APP_67001_COLLABS',
          status: 'active',
          members: 12,
        },
      ],
      github: {
        name: 'ai-customer-insights',
        location: 'https://github.com/comcast/ai-customer-insights',
        status: 'active',
        visibility: 'private',
      },
      cnap: {
        name: 'exp-ai-customer-insights',
        location: 'https://cnap.comcast.com/tenancy/exp-ai-customer-insights',
        status: 'active',
        region: 'us-east-1',
      },
    },
  },
  {
    applicationId: 67002,
    name: 'Smart Recommendation Engine',
    description:
      'Testing personalized content recommendations using collaborative filtering',
    status: 'Experimental',
    owner: {
      login: 'jane_smith',
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane_smith@comcast.com',
    },
    createdAt: '2025-10-18T14:15:00Z',
    updatedAt: '2025-10-18T14:15:00Z',
    resources: {
      adGroups: [
        {
          name: 'APP_67002_ADMIN',
          location: 'https://ad.comcast.com/group/APP_67002_ADMIN',
          status: 'active',
          members: 2,
        },
        {
          name: 'APP_67002_USERS',
          location: 'https://ad.comcast.com/group/APP_67002_USERS',
          status: 'active',
          members: 4,
        },
        {
          name: 'APP_67002_COLLABS',
          location: 'https://ad.comcast.com/group/APP_67002_COLLABS',
          status: 'active',
          members: 8,
        },
      ],
      github: {
        name: 'smart-recommendation-engine',
        location: 'https://github.com/comcast/smart-recommendation-engine',
        status: 'active',
        visibility: 'private',
      },
      cnap: {
        name: 'exp-smart-recommendation',
        location: 'https://cnap.comcast.com/tenancy/exp-smart-recommendation',
        status: 'active',
        region: 'us-west-2',
      },
    },
  },
  {
    applicationId: 67003,
    name: 'Network Performance Monitor',
    description: 'Proof of concept for real-time network diagnostics dashboard',
    status: 'Experimental',
    owner: {
      login: 'bob_johnson',
      firstName: 'Bob',
      lastName: 'Johnson',
      email: 'bob_johnson@comcast.com',
    },
    createdAt: '2025-10-20T09:00:00Z',
    updatedAt: '2025-10-20T09:00:00Z',
    resources: {
      adGroups: [
        {
          name: 'APP_67003_ADMIN',
          location: 'https://ad.comcast.com/group/APP_67003_ADMIN',
          status: 'active',
          members: 2,
        },
        {
          name: 'APP_67003_USERS',
          location: 'https://ad.comcast.com/group/APP_67003_USERS',
          status: 'active',
          members: 3,
        },
        {
          name: 'APP_67003_COLLABS',
          location: 'https://ad.comcast.com/group/APP_67003_COLLABS',
          status: 'active',
          members: 6,
        },
      ],
      github: {
        name: 'network-performance-monitor',
        location: 'https://github.com/comcast/network-performance-monitor',
        status: 'active',
        visibility: 'private',
      },
      cnap: {
        name: 'exp-network-perf-monitor',
        location: 'https://cnap.comcast.com/tenancy/exp-network-perf-monitor',
        status: 'provisioning',
        region: 'us-east-1',
      },
    },
  },
];

/**
 * Type for the application data returned from the GraphQL query
 */
export type ExperimentalAppQueryResult = {
  applicationId: number;
  name: string;
  description: string;
  status: string;
  owner?: {
    displayName?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  createdAt?: string;
  updatedAt?: string;
  resources?: {
    adGroupsPaged?: {
      totalCount?: number;
      adGroups?: Array<{
        groupId: string;
        groupName: string;
        description?: string;
      }>;
    };
  };
};

/**
 * Maps an application from the GraphQL query to ExperimentalApplication format
 * Includes AD Group resources with Azure portal URLs
 * @param app - The application data from the GraphQL query
 * @returns The mapped ExperimentalApplication object
 */
export const mapToExperimentalApplication = (
  app: ExperimentalAppQueryResult,
): ExperimentalApplication => {
  // Parse owner name from displayName or construct from firstName/lastName
  let firstName = 'Unknown';
  let lastName = 'User';
  const email = app.owner?.email || 'unknown@comcast.com';

  if (app.owner?.firstName && app.owner?.lastName) {
    firstName = app.owner.firstName;
    lastName = app.owner.lastName;
  } else if (app.owner?.displayName) {
    const nameParts = app.owner.displayName.split(' ');
    if (nameParts.length >= 2) {
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(' ');
    } else {
      firstName = app.owner.displayName;
    }
  }

  // Map AD Groups with Azure portal URLs
  const adGroups: ADGroupResource[] = [];
  if (app.resources?.adGroupsPaged?.adGroups) {
    app.resources.adGroupsPaged.adGroups.forEach(group => {
      adGroups.push({
        name: group.groupName,
        location: `https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/${group.groupId}`,
        status: 'active',
      });
    });
  }

  // If no AD groups exist, shim in dummy data for testing/demo purposes
  if (adGroups.length === 0) {
    const dummyGroups = [
      {
        groupId: 'c9e56bb3-d1d1-46fe-a840-c0ab40ebe907',
        groupName: 'COLLABORATORS',
      },
      {
        groupId: '12cc522b-7139-485f-b40d-e9e3878346cd',
        groupName: 'ADMINS',
      },
      {
        groupId: '38f37e86-c3d0-47b4-97c6-0c880c69e10b',
        groupName: 'USERS',
      },
    ];

    dummyGroups.forEach(group => {
      adGroups.push({
        name: `DEV_APP_${app.applicationId}_${group.groupName}`,
        location: `https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/${group.groupId}`,
        status: 'active',
      });
    });
  }

  // Map GitHub repositories if available, otherwise shim in mock data
  const githubRepos: GitHubResource[] = [];
  // TODO: Add real GitHub repo mapping when backend provides data
  // For now, shim in mock data for all apps
  if (githubRepos.length === 0) {
    githubRepos.push({
      name: 'test-monorepo',
      slug: 'comcast-enterprise/cool-app',
      location: 'https://github.com/comcast-enterprise/test-monorepo',
      status: 'active',
    });
  }

  // Map CNAP tenancy if available, otherwise shim in mock data
  let cnapTenancy: CNAPResource | undefined;
  // TODO: Add real CNAP mapping when backend provides data
  // For now, shim in mock data for all apps
  if (!cnapTenancy) {
    const cnapGettingStartedUrl = ConfigHelper.instance.config.getString(
      'app.cnapGettingStartedUrl',
    );

    cnapTenancy = {
      name: 'devhub-app',
      location: cnapGettingStartedUrl,
      status: 'active',
    };
  }

  const mappedResult: ExperimentalApplication = {
    applicationId: app.applicationId,
    name: app.name,
    description: app.description || 'No description available',
    status: 'Experimental',
    owner: {
      login: `${firstName.toLowerCase()}_${lastName.toLowerCase()}`,
      firstName,
      lastName,
      email,
    },
    createdAt: app.createdAt || new Date().toISOString(),
    updatedAt: app.updatedAt || new Date().toISOString(),
    resources: {
      adGroups: adGroups.length > 0 ? adGroups : undefined,
      githubRepos: githubRepos.length > 0 ? githubRepos : undefined,
      cnap: cnapTenancy,
    },
  };

  // eslint-disable-next-line no-console
  console.log('Mapper result for app', app.applicationId, ':', mappedResult);
  // eslint-disable-next-line no-console
  console.log('Resources:', mappedResult.resources);
  // eslint-disable-next-line no-console
  console.log('GitHub Repos:', mappedResult.resources.githubRepos);

  return mappedResult;
};

/**
 * Maps an idea from the new API format to ExperimentalApplication format
 * @param idea - The idea data from the new getIdeas() API
 * @returns The mapped ExperimentalApplication object
 */
export const mapIdeaToExperimentalApplication = (
  idea: IdeaFromAPI,
): ExperimentalApplication => {
  // Map AD Groups to the expected format
  const adGroups: ADGroupResource[] =
    idea.resources.adGroups?.map(group => ({
      name: group.groupName,
      location: `https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/${group.groupId}`,
      status: 'active' as const,
    })) || [];

  // Map GitHub repository if available
  const github: GitHubResource | undefined = idea.resources.githubRepo
    ? {
        name: idea.resources.githubRepo,
        slug: extractGitHubSlug(idea.resources.githubRepoUrl),
        location: idea.resources.githubRepoUrl || '#',
        status: 'active' as const,
        visibility: 'private' as const,
      }
    : undefined;

  // Map CNAP tenancy if available
  const cnap: CNAPResource | undefined = idea.resources.tenantName
    ? {
        name: idea.resources.tenantName,
        location: 'https://cnap.comcast.net/docs/cnap/',
        status: 'active' as const,
      }
    : undefined;

  // Parse user name from userLogin or owner (assuming format like "firstname_lastname")
  const ownerLogin = idea.owner || idea.userLogin;
  const nameParts = ownerLogin.split('_');
  const firstName = nameParts[0] || 'Unknown';
  const lastName = nameParts.slice(1).join(' ') || 'User';

  return {
    applicationId: idea.applicationId,
    name: idea.applicationName,
    description: idea.description, // Default description since not provided by API
    status: 'Experimental',
    owner: {
      login: ownerLogin,
      firstName,
      lastName,
      email: `${ownerLogin}@comcast.com`,
    },
    createdAt: idea.createdAt,
    updatedAt: idea.updatedAt,
    resources: {
      adGroups: adGroups.length > 0 ? adGroups : undefined,
      github,
      cnap,
    },
  };
};
